import { createRouter, createWebHistory } from 'vue-router'

import { appApi } from '@/api/app'
import { useAuthStore } from '@/stores/auth'

/**
 * 路由分组（前端方案 §1.2）：
 * - /          公开门户（PublicLayout），免登录浏览职位与导师
 * - /jobs      公开职位搜索与详情
 * - /mentors   公开导师服务展示
 * - /app/**   求职者端，挂载 CandidateLayout，仅 ROLE_CANDIDATE
 * - /mentor/** 导师端，挂载 MentorLayout，仅 ROLE_MENTOR
 * 守卫与后端权限双保险：后端以 X-User-Id 鉴权为准，前端守卫仅为体验层。
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // ==================== 公开门户（引流层） ====================
    {
      path: '/',
      component: () => import('@/layouts/public/PublicLayout.vue'),
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('@/pages/public/HomePage.vue'),
          meta: { title: '首页', public: true },
        },
        {
          path: 'jobs',
          name: 'public-jobs',
          component: () => import('@/pages/public/JobsPage.vue'),
          meta: { title: '职位搜索', public: true },
        },
        {
          path: 'jobs/:id',
          name: 'public-job-detail',
          component: () => import('@/pages/public/JobDetailPage.vue'),
          meta: { title: '职位详情', public: true },
        },
        {
          path: 'mentors',
          name: 'public-mentors',
          component: () => import('@/pages/public/MentorsPage.vue'),
          meta: { title: '导师服务', public: true },
        },
        {
          path: 'ai-mentor-match',
          name: 'public-ai-mentor-match',
          component: () => import('@/pages/public/AiMentorMatchPage.vue'),
          meta: { title: 'AI 导师匹配', public: true },
        },
        {
          path: 'mentors/:id',
          name: 'public-mentor-detail',
          component: () => import('@/pages/public/MentorDetailPage.vue'),
          meta: { title: '导师详情', public: true },
        },
        {
          path: 'events',
          name: 'public-events',
          component: () => import('@/pages/public/EventsPage.vue'),
          meta: { title: '活动', public: true },
        },
        {
          path: 'events/:id',
          name: 'public-event-detail',
          component: () => import('@/pages/public/EventDetailPage.vue'),
          meta: { title: '活动详情', public: true },
        },
        {
          path: 'group-sessions',
          name: 'public-group-sessions',
          component: () => import('@/pages/public/GroupSessionsPage.vue'),
          meta: { title: '小组讨论', public: true },
        },
        {
          path: 'group-sessions/:id',
          name: 'public-group-session-detail',
          component: () => import('@/pages/public/GroupSessionDetailPage.vue'),
          meta: { title: '小组讨论详情', public: true },
        },
        {
          path: 'p/:slug',
          name: 'public-site-page',
          component: () => import('@/pages/public/SitePage.vue'),
          meta: { title: '页面详情', public: true },
        },
      ],
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/login/LoginPage.vue'),
    },
    {
      path: '/onboarding/identity',
      name: 'onboarding-identity',
      component: () => import('@/pages/login/IdentitySelectPage.vue'),
      meta: { requiresAuth: true },
    },
    // ==================== 求职者端（需登录） ====================
    {
      path: '/app',
      component: () => import('@/layouts/candidate/CandidateLayout.vue'),
      meta: { role: 'ROLE_CANDIDATE' },
      children: [
        { path: '', redirect: '/app/workspace' },
        {
          path: 'workspace',
          name: 'app-workspace',
          component: () => import('@/pages/candidate/WorkspacePage.vue'),
          meta: { title: '工作台' },
        },
        {
          path: 'profile',
          name: 'app-profile',
          component: () => import('@/pages/candidate/ProfilePage.vue'),
          meta: { title: '职业画像' },
        },
        {
          path: 'materials',
          name: 'app-materials',
          component: () => import('@/pages/candidate/MaterialsPage.vue'),
          meta: { title: '材料工坊' },
        },
        {
          path: 'jobs',
          name: 'app-jobs',
          component: () => import('@/pages/candidate/JobsPage.vue'),
          meta: { title: '职位' },
        },
        {
          path: 'jobs/recommendations',
          name: 'app-recommendations',
          component: () => import('@/pages/candidate/RecommendationsPage.vue'),
          meta: { title: '每日推荐' },
        },
        {
          path: 'jobs/favorites',
          name: 'app-favorites',
          component: () => import('@/pages/candidate/FavoritesPage.vue'),
          meta: { title: '我的收藏' },
        },
        {
          path: 'jobs/:id',
          name: 'app-job-detail',
          component: () => import('@/pages/candidate/JobDetailPage.vue'),
          meta: { title: '职位详情' },
        },
        {
          path: 'pipeline',
          name: 'app-pipeline',
          component: () => import('@/pages/candidate/PipelinePage.vue'),
          meta: { title: '投递看板' },
        },
        {
          path: 'cart',
          name: 'app-cart',
          component: () => import('@/pages/candidate/CartPage.vue'),
          meta: { title: '申请篮' },
        },
        {
          path: 'cart/authorize',
          name: 'app-authorize',
          component: () => import('@/pages/candidate/AuthorizePage.vue'),
          meta: { title: '授权中心' },
        },
        {
          path: 'submit-confirm',
          name: 'app-submit-confirm',
          component: () => import('@/pages/candidate/SubmitConfirmPage.vue'),
          meta: { title: '提交前确认' },
        },
        {
          path: 'agent',
          name: 'app-agent',
          component: () => import('@/pages/candidate/AgentChatPage.vue'),
          meta: { title: 'AI 助手' },
        },
        {
          path: 'bookings',
          name: 'app-bookings',
          component: () => import('@/pages/candidate/MyBookingsPage.vue'),
          meta: { title: '我的预约' },
        },
        {
          path: 'meetings',
          name: 'app-meetings',
          component: () => import('@/pages/candidate/MeetingsPage.vue'),
          meta: { title: '我的会议' },
        },
        {
          path: 'bookings/create/:mentorId',
          name: 'app-booking-create',
          component: () => import('@/pages/candidate/BookingCreatePage.vue'),
          meta: { title: '预约导师' },
        },
        {
          path: 'orders',
          name: 'app-orders',
          component: () => import('@/pages/candidate/MyOrdersPage.vue'),
          meta: { title: '我的订单' },
        },
        {
          path: 'checkout',
          name: 'app-checkout',
          component: () => import('@/pages/candidate/CheckoutPage.vue'),
          meta: { title: '订单结算' },
        },
        {
          path: 'mentor-apply',
          name: 'app-mentor-apply',
          component: () => import('@/pages/candidate/MentorApplyPage.vue'),
          meta: { title: '申请成为导师' },
        },
        {
          path: 'training',
          name: 'app-training',
          component: () => import('@/pages/candidate/InterviewPracticePage.vue'),
          meta: { title: '面试训练' },
        },
        {
          path: 'training/history',
          name: 'app-training-history',
          component: () => import('@/pages/candidate/InterviewHistoryPage.vue'),
          meta: { title: '面试训练记录' },
        },
        {
          path: 'assessment',
          name: 'app-assessment',
          component: () => import('@/pages/candidate/AssessmentPracticePage.vue'),
          meta: { title: '测评准备' },
        },
        {
          path: 'calendar',
          name: 'app-calendar',
          component: () => import('@/pages/candidate/CalendarPage.vue'),
          meta: { title: '我的日历' },
        },
        {
          path: 'inbox',
          name: 'app-inbox',
          component: () => import('@/pages/candidate/InboxPage.vue'),
          meta: { title: '招聘收件箱' },
        },
        {
          path: 'notifications',
          name: 'app-notifications',
          component: () => import('@/pages/candidate/NotificationsPage.vue'),
          meta: { title: '通知中心' },
        },
        {
          path: 'notification-preferences',
          name: 'app-notification-preferences',
          component: () => import('@/pages/candidate/NotificationPreferencesPage.vue'),
          meta: { title: '通知偏好' },
        },
        {
          path: 'settings',
          name: 'app-settings',
          component: () => import('@/pages/candidate/SettingsPage.vue'),
          meta: { title: '账户设置' },
        },
        {
          path: 'invoices',
          name: 'app-invoices',
          component: () => import('@/pages/candidate/MyInvoicesPage.vue'),
          meta: { title: '我的发票' },
        },
        {
          path: 'orders/:orderId/invoice',
          name: 'app-invoice-apply',
          component: () => import('@/pages/candidate/InvoiceApplyPage.vue'),
          meta: { title: '申请发票' },
        },
        {
          path: 'settings/invoice-titles',
          name: 'app-invoice-titles',
          component: () => import('@/pages/candidate/InvoiceTitlesPage.vue'),
          meta: { title: '发票抬头管理' },
        },
      ],
    },
    {
      path: '/mentor',
      component: () => import('@/layouts/mentor/MentorLayout.vue'),
      meta: { role: 'ROLE_MENTOR' },
      children: [
        { path: '', redirect: '/mentor/workbench' },
        {
          path: 'workbench',
          name: 'mentor-workbench',
          component: () => import('@/pages/mentor/WorkbenchPage.vue'),
          meta: { title: '工作台' },
        },
        {
          path: 'services',
          name: 'mentor-services',
          component: () => import('@/pages/mentor/ServicesPage.vue'),
          meta: { title: '服务管理' },
        },
        {
          path: 'schedule',
          name: 'mentor-schedule',
          component: () => import('@/pages/mentor/SchedulePage.vue'),
          meta: { title: '排期管理' },
        },
        {
          path: 'bookings',
          name: 'mentor-bookings',
          component: () => import('@/pages/mentor/BookingsPage.vue'),
          meta: { title: '预约管理' },
        },
        {
          path: 'meetings',
          name: 'mentor-meetings',
          component: () => import('@/pages/mentor/MeetingsPage.vue'),
          meta: { title: '会议管理' },
        },
        {
          path: 'interview-coaching',
          name: 'mentor-interview-coaching',
          component: () => import('@/pages/mentor/InterviewCoachingPage.vue'),
          meta: { title: '面试辅导' },
        },
        {
          path: 'reviews',
          name: 'mentor-reviews',
          component: () => import('@/pages/mentor/ReviewsPage.vue'),
          meta: { title: '评价管理' },
        },
        {
          path: 'settlements',
          name: 'mentor-settlements',
          component: () => import('@/pages/mentor/SettlementsPage.vue'),
          meta: { title: '收入结算' },
        },
        {
          path: 'reports',
          name: 'mentor-reports',
          component: () => import('@/pages/mentor/ReportsPage.vue'),
          meta: { title: '数据报表' },
        },
        {
          // 复用求职者端申请组件：未建档的导师角色用户从报表页空态进入完成建档
          // （/app 有 ROLE_CANDIDATE 互斥守卫，导师角色无法直达 /app/mentor-apply）
          path: 'onboarding',
          name: 'mentor-onboarding',
          component: () => import('@/pages/candidate/MentorApplyPage.vue'),
          meta: { title: '完成导师建档' },
        },
        {
          path: 'profile',
          name: 'mentor-profile',
          component: () => import('@/pages/mentor/ProfilePage.vue'),
          meta: { title: '个人档案' },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/pages/NotFoundPage.vue'),
      meta: { title: '页面不存在' },
    },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // 刷新后会话恢复：有 token 但用户信息未加载时拉取 /me（失败自动清会话，下方按未登录处理）
  if (auth.isAuthenticated && !auth.user) {
    await auth.restore()
  }

  // 登录页：已登录则重定向回所属端首页
  if (to.name === 'login') {
    return auth.isAuthenticated ? { path: auth.homePath } : true
  }

  // 公开路由（meta.public）不检查认证
  if (to.meta.public) {
    return true
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // 身份选择页需要认证但不检查 identity_type
  if (to.name === 'onboarding-identity') {
    return true
  }

  // 双端互斥：角色不匹配时送回所属端首页
  const requiredRole = to.matched.find((record) => record.meta.role)?.meta.role
  if (requiredRole && auth.role !== requiredRole) {
    return { path: auth.homePath }
  }

  // 求职者冷启动：画像缺少 identity_type 时引导至身份选择页（跳过公开/求职端非核心页面）
  if (
    auth.role === 'ROLE_CANDIDATE' &&
    auth.user &&
    !to.path.startsWith('/onboarding') &&
    !to.path.startsWith('/mentor') &&
    to.name !== 'app-profile'
  ) {
    const SESSION_FLAG = '__identity_redirect_count'
    const maxRedirects = 2
    const count = Number(sessionStorage.getItem(SESSION_FLAG) ?? '0')
    try {
      const profile = await appApi.profile.getProfile()
      if (!profile?.basic?.identity_type) {
        if (count >= maxRedirects) {
          // 安全网：已达重定向上限，清除标记并放行，避免死循环
          sessionStorage.removeItem(SESSION_FLAG)
          return true
        }
        sessionStorage.setItem(SESSION_FLAG, String(count + 1))
        return { name: 'onboarding-identity' }
      }
      // identity_type 已设置，清除计数
      sessionStorage.removeItem(SESSION_FLAG)
    } catch {
      // 画像加载失败不阻塞路由
      sessionStorage.removeItem(SESSION_FLAG)
    }
  }

  return true
})

router.afterEach((to) => {
  const title = to.meta.title
  document.title = title ? `${title} · Sugar Link` : 'Sugar Link'
})

export default router
